import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  Dropdown,
  Heading,
  TextInput
} from '@takecasper/acuity-design-system';
import {
  ICreateAdminData,
  ICreateAdminVariables,
  ICreateUserData,
  ICreateUserVariables
} from '@types';
import validator from 'validator';
import { ToastBody } from 'components';
import sanitized from 'validations/sanitized';
import { LANGUAGE, ROUTES } from 'shared/enums';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  CreateAccountLayout,
  AnalyticsCheckboxWrapper
} from './create-account-form.styles';
import {
  TextInputWrapper,
  NumberedList,
  AlertStyled
} from 'shared/styled-components/users.styles';
import { ITriggerPageLeave } from 'shared/hooks/types';
import { IUser } from '@types';
import {
  useBlockExternalNavigation,
  useBlockInternalNavigation
} from 'shared/hooks';
import { ConfirmNavigationModal } from 'shared/components';
import { CreateAccountProgramSelect } from './create-account-program-select';
import { CREATE_ADMIN, CREATE_USER } from 'graphql/mutations/user';
import { setManageUsersFormValidationErrors } from 'shared/helpers/setManageUsersFormValidationErrors';
import { showAnalyticsPrompt } from './helpers/showAnalyticsPrompt';

interface ICreateAccountForm {
  isCreatingAdmin: boolean;
  user: IUser;
}

/**
 * Form to create a new user. If isCreatingAdmin is true, the program selector will not be
 * displayed and the form will be used to create an admin user.
 *
 * @param isCreatingAdmin - Flag to indicate if the form is for creating an admin user
 * @param user - The user object of the editor
 * @returns
 */
const CreateAccountForm = ({ isCreatingAdmin, user }: ICreateAccountForm) => {
  const [showAlertError, setShowAlertError] = useState<boolean>(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    setValue,
    setError
  } = useForm();

  const selectedPrograms = useWatch({
    control,
    name: 'programs',
    defaultValue: []
  });

  const showCreateAnalyticsPrompt = useMemo(() => {
    return showAnalyticsPrompt({
      user,
      programs: selectedPrograms
    });
  }, [selectedPrograms]);

  // State to manage the trigger for page leave confirmation
  // and to block navigation if the form is dirty.
  const [triggerPageChange, setTriggerPageLeave] = useState<ITriggerPageLeave>({
    onConfirm: false,
    path: ''
  });
  const [blockNavigation, setBlockNavigation] = useState(false);
  const [showConfirmNavigationModal, setShowConfirmNavigationModal] =
    useState(false);
  useBlockInternalNavigation({
    blockNavigation,
    triggerPageChange,
    setTriggerPageLeave,
    setShowConfirmNavigationModal
  });
  useBlockExternalNavigation({ blockNavigation });
  useEffect(() => {
    setBlockNavigation(isDirty);
  }, [isDirty]);

  const [createAdmin, { loading: createAdminLoading }] = useMutation<
    ICreateAdminData,
    ICreateAdminVariables
  >(CREATE_ADMIN, {
    onCompleted: () => {
      toast.success(
        <ToastBody
          category="success"
          title={t?.('common_success')}
          text={t?.('create_admin_success')}
          data-testid="success-toast"
        />
      );

      setTriggerPageLeave({ path: ROUTES.MANAGE_USERS.path, onConfirm: true });
    },
    onError: e => {
      const mutationErrorPayload =
        e.graphQLErrors[0]?.extensions?.exception?.payload;
      if (!mutationErrorPayload) {
        toast.error(
          <ToastBody
            category="error"
            title={t?.('common_something_went_wrong')}
            text={t?.('common_please_try_again')}
            data-testid="error-toast"
          />
        );
      } else {
        setShowAlertError(true);
        setManageUsersFormValidationErrors(mutationErrorPayload, setError, t);
      }
    },
    update: (cache, { data: { createAdmin } }) => {
      cache.modify({
        fields: {
          getUsers(getUsers = []) {
            return [createAdmin, ...getUsers];
          }
        }
      });
    }
  });

  const [createUser, { loading: createUserLoading }] = useMutation<
    ICreateUserData,
    ICreateUserVariables
  >(CREATE_USER, {
    onCompleted: ({ createUser }) => {
      const createdUserSchools = createUser.schoolPermissions.map(
        schoolPermission => schoolPermission.school
      );

      toast.success(
        <ToastBody
          category="success"
          title={t?.('common_success')}
          text={t?.('create_user_success')}
          data-testid="success-toast"
        />
      );

      // If the created user doesn't have all selected programs, we can infer the programs failed to be added.
      const failedPrograms = selectedPrograms.filter(
        program =>
          !createdUserSchools.some(school => school._id === program.value)
      );
      if (failedPrograms.length > 0) {
        toast.warning(
          <ToastBody
            category="alert"
            title={t?.('common_warning')}
            text={t?.('add_program_access_error', {
              name: createUser.profile.name,
              programs: failedPrograms.map(school => school.label).join(', ')
            })}
            data-testid="create-user-failed-to-add-programs-toast"
          />
        );
      }

      setTriggerPageLeave({
        path: `${ROUTES.EDIT_USER.path}/${createUser._id}?view=${createdUserSchools[0]._id}&product=general`,
        onConfirm: true
      });
    },
    onError: e => {
      const mutationErrorPayload =
        e.graphQLErrors[0]?.extensions?.exception?.payload;
      if (!mutationErrorPayload) {
        toast.error(
          <ToastBody
            category="error"
            title={t?.('common_something_went_wrong')}
            text={t?.('common_please_try_again')}
            data-testid="error-toast"
          />
        );
      } else {
        setShowAlertError(true);
        setManageUsersFormValidationErrors(mutationErrorPayload, setError, t);
      }
    },
    update: (cache, { data: { createUser } }) => {
      cache.modify({
        fields: {
          getUsers(getUsers = []) {
            return [createUser, ...getUsers];
          }
        }
      });
    }
  });

  const onSubmit = data => {
    if (isCreatingAdmin) {
      createAdmin({
        variables: {
          email: data.email,
          givenname: data.givenname,
          surname: data.surname,
          lang: data.lang
        }
      });
    } else {
      createUser({
        variables: {
          email: data.email,
          givenname: data.givenname,
          surname: data.surname,
          lang: data.lang,
          schoolIds: selectedPrograms.map(({ value }) => value),
          providerid: data.providerid,
          createAnalyticsAccount: data?.createAnalyticsAccount || false
        }
      });
    }
  };

  // After a user has cleared in-line validation, we don't want to show the error alert
  // until the user submits the form again with errors.
  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setShowAlertError(false);
    }
  }, [Object.keys(errors).length]);

  const onSubmitError = () => {
    setShowAlertError(true);
  };

  return (
    <CreateAccountLayout>
      <Heading text={t?.('account_details')} hasMarginBottom={false} />
      {isCreatingAdmin && (
        <AlertStyled
          isDismissible={false}
          id="create-admin-warning-alert"
          data-testid="create-admin-warning-alert"
          title={t?.('common_heads_up')}
          description={t?.('create_admin_alert_description')}
          variant="warning"
          titleSize="small"
        />
      )}
      {showAlertError && (
        <AlertStyled
          isDismissible={false}
          id="create-account-error-alert"
          data-testid="create-account-error-alert"
          data-analytics-id="create-account-error-alert"
          variant="error"
          title={t?.('common_form_validation_error_title')}
          description={t?.('common_form_validation_error_description')}
          content={
            <NumberedList>
              {Object.values(errors).map((error, index) => {
                return (
                  <li key={`${index}-${error.message}`}>
                    {error.message as string}
                  </li>
                );
              })}
            </NumberedList>
          }
        />
      )}
      <form
        onSubmit={handleSubmit(onSubmit, onSubmitError)}
        data-testid="create-account-form"
        data-analytics-id="create-account-form"
      >
        <TextInputWrapper>
          <TextInput
            {...register('givenname', {
              validate: {
                required: value =>
                  !validator.isEmpty(value.trim()) ||
                  t?.('input_first_name_required'),
                sanitized: value =>
                  sanitized(value) || t?.('input_first_name_invalid')
              }
            })}
            id="create_account_first_name"
            data-testid="create_account_first_name"
            data-analytics-id="create_account_first_name"
            label={t?.('account_information_first_name')}
            state={errors.givenname ? 'error' : 'default'}
            message={errors.givenname && (errors.givenname.message as string)}
          />
          <TextInput
            {...register('surname', {
              validate: {
                required: value =>
                  !validator.isEmpty(value.trim()) ||
                  t?.('input_last_name_required'),
                sanitized: value =>
                  sanitized(value) || t?.('input_last_name_invalid')
              }
            })}
            id="create_account_last_name"
            data-testid="create_account_last_name"
            data-analytics-id="create_account_last_name"
            label={t?.('account_information_last_name')}
            state={errors.surname ? 'error' : 'default'}
            message={errors.surname && (errors.surname.message as string)}
          />
          <TextInput
            {...register('email', {
              validate: {
                required: value =>
                  !validator.isEmpty(value.trim()) ||
                  t?.('input_email_required'),
                sanitized: value =>
                  sanitized(value) || t?.('input_email_invalid'),
                valid: value =>
                  validator.isEmail(value) || t?.('input_email_invalid')
              }
            })}
            id="create_account_email"
            data-testid="create_account_email"
            data-analytics-id="create_account_email"
            label={t?.('input_email')}
            state={errors.email ? 'error' : 'default'}
            message={errors.email && (errors.email.message as string)}
          />
          {!isCreatingAdmin && (
            <CreateAccountProgramSelect
              errors={errors}
              register={register}
              setValue={setValue}
              selectedPrograms={selectedPrograms}
              creator={user}
            />
          )}

          {!isCreatingAdmin && (
          <TextInput
            {...register('providerid', {})}
            id="create_account_providerid"
            data-testid="create_account_providerid"
            data-analytics-id="create_account_providerid"
            label={t?.('input_providerid')}
          />    
          )}

          {showCreateAnalyticsPrompt && !isCreatingAdmin && (
            <Controller
              name="createAnalyticsAccount"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <AnalyticsCheckboxWrapper>
                  <Checkbox
                    id="create_analytics_account_prompt"
                    data-testid="create_analytics_account_prompt"
                    data-analytics-id="create_analytics_account_prompt"
                    label={t?.('create_analytics_account_prompt')}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                </AnalyticsCheckboxWrapper>
              )}
            />
          )}
          <Dropdown
            id="create_account_language"
            data-testid="create_account_language"
            data-analytics-id="create_account_language"
            label={t?.('account_language')}
            message={errors.lang && (errors.lang.message as string)}
            state={errors.lang ? 'error' : 'default'}
            {...register('lang')}
          >
            {Object.values(LANGUAGE).map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </Dropdown>
          <Button
            data-analytics-id="create_account_save_button"
            data-testid="create_account_save_button"
            text={
              isCreatingAdmin
                ? t?.('create_admin_account')
                : t?.('create_user_account')
            }
            type="submit"
            stateful={createAdminLoading || createUserLoading}
            trailingIcon={createAdminLoading || createUserLoading}
            onClick={() => {}}
            disabled={
              !isDirty ||
              createAdminLoading ||
              createUserLoading ||
              Object.keys(errors).length > 0
            }
          />
        </TextInputWrapper>
      </form>
      {showConfirmNavigationModal && (
        <ConfirmNavigationModal
          handleOnLeaveClick={() => {
            setTriggerPageLeave(obj => ({ ...obj, onConfirm: true }));
            setShowConfirmNavigationModal(false);
          }}
          handleOnStayClick={() => {
            setTriggerPageLeave(obj => ({ ...obj, onConfirm: false }));
            setShowConfirmNavigationModal(false);
          }}
        />
      )}
    </CreateAccountLayout>
  );
};

export default CreateAccountForm;
